import type {
  IComparisonFilter,
  IFilter,
  IRangeFilter,
  ITextFilter,
  ListingsAPIAndFilter,
  ListingsAPIOrFilter,
} from '../types/ListingsAPIFilter';

class BaseFilterBuilder<T extends { negated: boolean }> {
  protected readonly data: T;

  constructor(data: T) {
    this.data = data;
  }

  negated(): this {
    this.data.negated = true;
    return this;
  }
}

export class ListingsRequestRangeFilterBuilder extends BaseFilterBuilder<IRangeFilter> {
  constructor(key: string) {
    super({
      key,
      negated: false,
    });
  }

  min(n: number, exclusive: boolean = false): this {
    this.data.minimum = n;
    this.data.exclusiveMinimum = exclusive;
    return this;
  }

  max(n: number, exclusive: boolean = false): this {
    this.data.maximum = n;
    this.data.exclusiveMaximum = exclusive;
    return this;
  }

  build(): IRangeFilter {
    return { ...this.data };
  }
}

export class ListingsRequestComparisonFilterBuilder extends BaseFilterBuilder<IComparisonFilter> {
  constructor(key: string, compareTo: number, comparisonMode: '<=' | '<' | '>=' | '>' | '=') {
    super({
      key,
      compareTo,
      comparisonMode,
      negated: false,
    });
  }

  build(): IComparisonFilter {
    return { ...this.data };
  }
}

export class ListingsRequestTextFilterBuilder extends BaseFilterBuilder<ITextFilter> {
  constructor(key: string, text: string[]) {
    super({
      key,
      text,
      negated: false,
    });
  }

  build(): ITextFilter {
    return { ...this.data };
  }
}

type FilterBuilder =
  | ListingsRequestAndFilterBuilder
  | ListingsRequestOrFilterBuilder
  | ListingsRequestTextFilterBuilder
  | ListingsRequestRangeFilterBuilder
  | ListingsRequestComparisonFilterBuilder;

export class ListingsRequestAndFilterBuilder {
  private readonly data: { and: IFilter[] } = { and: [] };

  addFilter(filterBuilder: FilterBuilder): this {
    this.data.and.push(filterBuilder.build());
    return this;
  }

  build(): ListingsAPIAndFilter {
    if (this.data.and.length === 0) {
      throw new Error('At least one filter must be added to the AND filter');
    }
    return { ...this.data };
  }
}

export class ListingsRequestOrFilterBuilder {
  private readonly data: { or: IFilter[] } = { or: [] };

  addFilter(filterBuilder: FilterBuilder): this {
    this.data.or.push(filterBuilder.build());
    return this;
  }

  build(): ListingsAPIOrFilter {
    if (this.data.or.length === 0) {
      throw new Error('At least one filter must be added to the OR filter');
    }
    return { ...this.data };
  }
}
